import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { useGetUserTrendingQuery } from '../../redux/thunks/users/gettrending';
import { useGetTopBusinessesQuery } from '../../redux/thunks/unauth/gettopbusinesses';
import PlacesAutocomplete from 'react-places-autocomplete';

import TrustSignals from './TrustSignals';

import { Helmet } from 'react-helmet-async';
import EnhancedSEO from '../../Components/SEO/EnhancedSEO';

import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  InputBase,
  TextField,
  Divider,
  IconButton,
  Chip,
  Skeleton,
  Collapse,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  ArrowForward as ArrowForwardIcon,
  People as PeopleIcon,
  Star as StarIcon,
  RestaurantOutlined as RestaurantIcon,
  Gavel as GavelIcon,
  LocalHospital as LocalHospitalIcon,
  MedicalServices as DentistIcon,
  Hotel as HotelIcon,
  StarsOutlined as RatedIcon,
  NewReleasesOutlined as NewIcon,
  SecurityOutlined as InsuranceIcon,
  ArrowUpward as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MyLocation as MyLocationIcon,
} from '@mui/icons-material';

const styles = `
.places-autocomplete-dropdown {
  position: absolute;
  z-index: 1000;
  width: 100%;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
  max-height: 200px;
}

.suggestion-item {
  padding: 8px 16px;
  cursor: pointer;
}

.suggestion-item:hover,
.suggestion-item-active {
  background-color: rgba(0, 0, 0, 0.08);
}
`;

const POPULAR_SEARCHES = [
  'Restaurants',
  'Coffee',
  'Dentists',
  'Attorney'
];

const CATEGORIES = [
  { 
    icon: RestaurantIcon, 
    name: 'Restaurants',
    color: 'primary.main'
  },
  { 
    icon: InsuranceIcon, 
    name: 'Insurance',
    color: 'secondary.main'
  },
  { 
    icon: GavelIcon,
    name: 'Legal',
    color: 'error.main'
  },
  { 
    icon: LocalHospitalIcon,
    name: 'Doctors',
    color: 'success.main'
  },
  { 
    icon: DentistIcon,
    name: 'Dentists',
    color: 'warning.main'
  },
  { 
    icon: HotelIcon, 
    name: 'Hotels',
    color: 'info.main'
  }
];
  
const QUICK_LINKS = [
  {
    icon: RatedIcon,
    title: 'Highly Rated Places',
    description: 'Top-rated businesses near you',
    color: 'warning.main',
    tag: 'highlyrated',
  },
  {
    icon: NewIcon,
    title: 'New on Revfer',
    description: 'Recently added businesses',
    color: 'success.main',
    tag: 'newbusiness',
  },
  {
    icon: TrendingUpIcon,
    title: 'Growing Businesses',
    description: 'Businesses with increasing popularity',
    color: 'info.main',
    tag: 'growingbusiness',
  }
];

const LoadingStateComponent = () => (
  <>
    <Box sx={{ mt: 3 }}>
      <Skeleton variant="text" width="200px" height={32} />
      <Paper variant='outlined' sx={{ p: 2, mt: 1, border: 'none' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} />
            <Skeleton variant="text" width="40%" height={20} />
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
              <Skeleton variant="rectangular" width={100} height={20} />
              <Skeleton variant="text" width={120} height={20} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} variant="circular" width={32} height={32} />
            ))}
          </Box>
        </Box>
        <Skeleton variant="text" width="40%" height={20} sx={{ mt: 1 }} />
      </Paper>
    </Box>

    <Box sx={{ mt: 4 }}>
      <Skeleton variant="text" width="150px" height={32} />
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {[1, 2, 3, 4].map((index) => (
          <Grid item xs={12} key={index}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                    <Skeleton variant="circular" width={24} height={24} />
                    <Skeleton variant="text" width="40%" height={20} />
                  </Box>
                  <Skeleton variant="text" width="70%" height={24} />
                  <Skeleton variant="text" width="50%" height={20} />
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                    <Skeleton variant="rectangular" width={100} height={20} />
                    <Skeleton variant="text" width={120} height={20} />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  </>
);

const TrendingAndTrustSignals = ({ trendingBusinesses, topBusinesses, isLoading }) => {
  if (isLoading) {
    return <LoadingStateComponent />;
  }

  return (
    <>
      <TrustSignals topBusinesses={topBusinesses} />
    </>
  );
};

const formatLocationDisplay = (addressComponents, fullDescription) => {
  if (!addressComponents || addressComponents.length === 0) {
    const parts = fullDescription.split(',').map(p => p.trim());
    if (parts.length >= 2) {
      return `${parts[0]}, ${parts[parts.length - 1]}`;
    }
    return fullDescription;
  }

  const getComponent = (types) => {
    return addressComponents.find(c => types.some(t => c.types.includes(t)));
  };

  const city = getComponent(['locality', 'administrative_area_level_2', 'postal_town'])?.long_name || '';
  const region = getComponent(['administrative_area_level_1'])?.short_name || '';
  const country = getComponent(['country'])?.short_name || '';

  if (country === 'US' || country === 'IN') {
    return city && region ? `${city}, ${region}, ${country}` : fullDescription;
  } else {
    const countryName = getComponent(['country'])?.long_name || country;
    return city ? `${city}, ${countryName}` : fullDescription;
  }
};

const SearchBar = ({ onSearch, isLoading }) => {
  const [query, setQuery] = useState('');
  const [location, setLocation] = useState('');
  const [isNaturalLanguageMode, setIsNaturalLanguageMode] = useState(false);
  const [locationError, setLocationError] = useState(false);
  const [isGeolocating, setIsGeolocating] = useState(false);
  const [geoError, setGeoError] = useState(null);
  const [countryError, setCountryError] = useState(null);


  useEffect(() => {
    detectUserLocation();
  }, []);

  const detectUserLocation = useCallback(() => {
    if (!navigator.geolocation) {
      console.log('Geolocation not supported');
      setGeoError('Geolocation not supported');
      return;
    }

    if (!window.google || !window.google.maps) {
      console.log('Google Maps not loaded yet');
      setGeoError('Maps not loaded');
      return;
    }

    setIsGeolocating(true);
    setGeoError(null);

    const timeoutId = setTimeout(() => {
      setIsGeolocating(false);
      console.log('Geolocation timed out');
    }, 15000);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const geocoder = new window.google.maps.Geocoder();
          
          geocoder.geocode(
            { location: { lat: latitude, lng: longitude } },
            (results, status) => {
              clearTimeout(timeoutId);
              
              if (status === 'OK' && results && results.length > 0) {
                const cityResult = results.find(r => 
                  r.types.includes('locality') || 
                  r.types.includes('administrative_area_level_2') ||
                  r.types.includes('postal_town')
                ) || results[0];

                if (cityResult) {
                  const formattedLocation = formatLocationDisplay(
                    cityResult.address_components,
                    cityResult.formatted_address
                  );
                  setLocation(formattedLocation);
                  console.log('Auto-detected location:', formattedLocation);
                }
              } else {
                console.log('Reverse geocoding failed:', status);
                setGeoError('Could not determine location');
              }
              setIsGeolocating(false);
            }
          );
        } catch (error) {
          clearTimeout(timeoutId);
          console.log('Reverse geocoding error:', error);
          setGeoError('Could not determine location');
          setIsGeolocating(false);
        }
      },
      (error) => {
        clearTimeout(timeoutId);
        console.log('Geolocation error:', error.message);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setGeoError('Location access denied');
            break;
          case error.POSITION_UNAVAILABLE:
            setGeoError('Location unavailable');
            break;
          case error.TIMEOUT:
            setGeoError('Location request timed out');
            break;
          default:
            setGeoError('Could not get location');
        }
        setIsGeolocating(false);
      },
      { 
        timeout: 10000,
        enableHighAccuracy: false,
        maximumAge: 300000
      }
    );
  }, []);

  const handleSearch = () => {
    if (!location.trim()) {
      setLocationError(true);
      return;
    }
    if (countryError) return;  // ← just add this line
    
    setLocationError(false);
    onSearch({ 
      query, 
      location,
      isNaturalLanguageSearch: isNaturalLanguageMode 
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleLocationChange = (address) => {
    setLocation(address);
    setCountryError(null);
    if (address.trim()) {
      setLocationError(false);
    }
  };
  
  const handleLocationSelect = async (address) => {
    setLocation(address);
    setLocationError(false);
    setCountryError(null);
  
    // Validate country support
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address }, (results, status) => {
        if (status === 'OK' && results?.[0]) {
          const countryComponent = results[0].address_components.find(
            c => c.types.includes('country')
          );
          const countryCode = countryComponent?.short_name;
          
          if (countryCode && countryCode !== 'US' && countryCode !== 'IN') {
            setCountryError(`We currently only support businesses in the US and India. ${countryComponent.long_name} is not yet supported.`);
          }
        }
      });
    }
  };
  const handleToggleSearchMode = (newMode) => {
    if (newMode !== isNaturalLanguageMode) {
      setQuery('');
      setIsNaturalLanguageMode(newMode);
    }
  };

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ p: 3, border: 'none' }}>
        <Skeleton variant="text" width="60%" height={40} />
        <Skeleton variant="text" width="40%" height={20} sx={{ mb: 2 }} />
        <Skeleton variant="rectangular" height={60} sx={{ borderRadius: 3 }} />
      </Paper>
    );
  }

  return (
    <Paper variant='outlined' sx={{ 
      p: { xs: 2, sm: 3 },
      border: 'none',
      position: 'relative'
    }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start',
        mb: 2,
        pt: { xs: 4, sm: 0 },
      }}>
        <Box sx={{ pr: { xs: 0, sm: 2 }, width: '100%' }}>
          <Typography variant="h5" gutterBottom component="h2">
            Find Trusted Businesses
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Discover businesses recommended by people you trust
          </Typography>
        </Box>
        
        <Box 
          sx={{ 
            display: 'flex', 
            bgcolor: 'grey.100', 
            borderRadius: 3, 
            overflow: 'hidden',
            border: 1,
            borderColor: 'grey.300',
            height: 32,
            alignItems: 'center',
            fontSize: '0.85rem',
            position: { xs: 'absolute', sm: 'relative' },
            top: { xs: 12, sm: 'auto' },
            right: { xs: 12, sm: 'auto' },
            zIndex: 1,
            minWidth: { sm: '240px' },
          }}
        >
          <Box 
            onClick={() => handleToggleSearchMode(false)}
            sx={{ 
              px: 2,
              py: 0.5,
              cursor: 'pointer',
              bgcolor: !isNaturalLanguageMode ? 'primary.main' : 'transparent',
              color: !isNaturalLanguageMode ? 'white' : 'text.primary',
              fontWeight: !isNaturalLanguageMode ? 'medium' : 'normal',
              transition: 'all 0.2s ease',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              '&:hover': {
                bgcolor: !isNaturalLanguageMode ? 'primary.dark' : 'grey.200'
              }
            }}
          >
            <Typography variant="caption" noWrap>Keywords</Typography>
          </Box>
          <Box 
            onClick={() => handleToggleSearchMode(true)}
            sx={{ 
              px: 2,
              py: 0.5,
              cursor: 'pointer',
              bgcolor: isNaturalLanguageMode ? 'primary.main' : 'transparent',
              color: isNaturalLanguageMode ? 'white' : 'text.primary',
              fontWeight: isNaturalLanguageMode ? 'medium' : 'normal',
              transition: 'all 0.2s ease',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              position: 'relative',
              '&:hover': {
                bgcolor: isNaturalLanguageMode ? 'primary.dark' : 'grey.200'
              }
            }}
          >
            <Typography variant="caption" noWrap>
              Natural Language
              <Box 
                component="span"
                sx={{
                  ml: 0.5,
                  px: 0.5,
                  borderRadius: 0.5,
                  bgcolor: isNaturalLanguageMode ? 'white' : 'primary.main',
                  color: isNaturalLanguageMode ? 'primary.main' : 'white',
                  fontSize: '0.65rem',
                  fontWeight: 'bold',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: 16,
                  verticalAlign: 'text-top',
                }}
              >
                AI
              </Box>
            </Typography>
          </Box>
        </Box>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: isNaturalLanguageMode ? '8px 4px' : '2px 4px',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: isNaturalLanguageMode ? 'flex-start' : 'center' },
          width: '100%',
          mt: { xs: 4, sm: 2 },
          bgcolor: 'grey.50',
          borderRadius: 3,
          position: 'relative',
          transition: 'all 0.3s ease',
          border: '1px solid',
          borderColor: 'grey.200',
          overflow: 'visible',
          minHeight: { sm: 50 },
          gap: { sm: 0.5, md: 0 },
        }}
      >
        <Box sx={{ 
          display: 'flex', 
          alignItems: isNaturalLanguageMode ? 'flex-start' : 'center',
          width: { xs: '100%', sm: '55%', md: '60%' },
          minWidth: 0,
          pl: 2,
          pt: isNaturalLanguageMode ? 1 : 0,
          pb: { xs: 1, sm: 0 },
        }}>
          <SearchIcon sx={{ 
            color: 'text.secondary', 
            mr: 1,
            mt: isNaturalLanguageMode ? 1 : 0,
            flexShrink: 0
          }} />
          <InputBase
            placeholder={isNaturalLanguageMode 
              ? "Describe what you're looking for..." 
              : "Search businesses or categories"}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyPress}
            sx={{ 
              flex: 1,
              minWidth: 0,
              '& .MuiInputBase-input': {
                transition: 'all 0.3s ease',
                height: isNaturalLanguageMode ? 'auto' : '1.4em',
                width: '100%'
              }
            }}
            multiline={isNaturalLanguageMode}
            minRows={isNaturalLanguageMode ? 2 : 1}
            maxRows={isNaturalLanguageMode ? 3 : 1}
            fullWidth
            aria-label="Search for businesses"
          />
        </Box>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ 
            display: { xs: 'none', sm: 'block' },
            mx: { sm: 1, md: 1.5 },
            alignSelf: 'stretch',
            flexShrink: 0
          }} 
        />
        
        <Divider
          orientation="horizontal"
          sx={{ 
            display: { xs: 'block', sm: 'none' },
            width: '92%',
            mx: 'auto',
            my: 1,
          }} 
        />

        <Box sx={{ 
          display: 'flex', 
          alignItems: { xs: 'center', sm: isNaturalLanguageMode ? 'flex-start' : 'center' }, 
          width: { xs: '100%', sm: '35%', md: '30%' },
          minWidth: { sm: '200px', md: '220px' },
          maxWidth: { sm: '280px', md: '300px' },
          flexShrink: 1,
          pt: { xs: 1, sm: isNaturalLanguageMode ? 1 : 0 },
          pb: { xs: 1, sm: 0 },
          px: 2, 
          position: 'relative'
        }}>
          {isGeolocating ? (
            <CircularProgress size={20} sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
          ) : (
            <Tooltip title="Detect my location">
              <IconButton 
                size="small" 
                onClick={detectUserLocation}
                sx={{ 
                  mr: 0.5, 
                  p: 0.5,
                  flexShrink: 0,
                  color: locationError ? 'error.main' : 'text.secondary',
                  '&:hover': {
                    color: 'primary.main'
                  }
                }}
              >
                <MyLocationIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          <PlacesAutocomplete
            value={location}
            onChange={handleLocationChange}
            onSelect={handleLocationSelect}
          >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading: placesLoading }) => (
              <div style={{ width: '100%', position: 'relative' }}>
                <TextField
                  {...getInputProps({
                    placeholder: isGeolocating ? "Detecting location..." : "Enter your location",
                  })}
                  disabled={isGeolocating}
                  fullWidth
                  variant="standard"
                  size="small"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  sx={{ 
                    flex: 1, 
                    width: '100%',
                    '& .MuiInputBase-input': {
                      color: locationError ? 'error.main' : 'inherit',
                      padding: '4px 0',
                    }
                  }}
                  aria-label="Enter location"
                />
                
                {suggestions.length > 0 && (
                  <Paper 
                    elevation={3} 
                    sx={{ 
                      position: 'absolute', 
                      zIndex: 1000, 
                      mt: 1, 
                      width: '100%',
                      minWidth: '280px',
                      maxHeight: 250,
                      overflowY: 'auto',
                      left: 0,
                    }}
                  >
                    {placesLoading && (
                      <Box sx={{ p: 2 }}>Loading...</Box>
                    )}
                    {suggestions.map((suggestion) => {
                      const { key, ...suggestionProps } = getSuggestionItemProps(suggestion);
                      return (
                        <Box
                          key={suggestion.placeId}
                          {...suggestionProps}
                          sx={{
                            p: 1.5,
                            cursor: 'pointer',
                            borderBottom: '1px solid #e0e0e0',
                            '&:hover': { bgcolor: 'grey.50' },
                            '&:last-child': { borderBottom: 'none' }
                          }}
                        >
                          <Typography variant="body2">
                            {suggestion.description}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Paper>
                )}
              </div>
            )}
          </PlacesAutocomplete>
        </Box>

        <Box sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          width: { xs: '100%', sm: 'auto' },
          px: { xs: 1.5, sm: 0.5, md: 1 },
          py: { xs: 1.5, sm: 0 },
          flexShrink: 0,
          ml: { sm: 0.5, md: 'auto' },
          alignSelf: 'center'
        }}>
          <IconButton 
            onClick={handleSearch}
            sx={{ 
              bgcolor: 'primary.main',
              color: 'white',
              borderRadius: 1,
              height: 36,
              width: 36,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: 'primary.dark',
              }
            }}
            aria-label="Search businesses"
          >
            <ArrowForwardIcon />
          </IconButton>
        </Box>
      </Paper>
      
      {locationError && (
        <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
          Please enter a location to search
        </Typography>
      )}

      {geoError && !location && (
        <Typography variant="caption" color="text.secondary" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
          {geoError}. Please enter your location manually.
        </Typography>
      )}

      {countryError && (
        <Typography variant="caption" color="error" sx={{ ml: 2, mt: 0.5, display: 'block' }}>
          {countryError}
        </Typography>
      )}

      {!isNaturalLanguageMode && (
        <Box sx={{ mt: 1, px: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'inline-block', mr: 1 }}>
            Popular searches:
          </Typography>
          <Box sx={{ display: 'inline-flex', gap: 0.5, flexWrap: 'wrap' }}>
            {POPULAR_SEARCHES.map((term) => (
              <Chip
                key={term}
                label={term}
                size="small"
                onClick={() => {
                  setQuery(term);
                }}
                sx={{ 
                  bgcolor: 'primary.50',
                  height: 24,
                  '& .MuiChip-label': {
                    px: 1,
                    fontSize: '0.7rem'
                  },
                  '&:hover': {
                    bgcolor: 'primary.100',
                  },
                  transition: 'all 0.2s ease',
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {isNaturalLanguageMode && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
            Try phrases like:
          </Typography>
          <Grid container spacing={1}>
            {[
              "A cozy coffee shop with strong wifi",
              "Family-friendly dentist open on weekends",
              "Best Italian restaurant for anniversary dinner",
              "Affordable immigration lawyer who speaks Spanish"
            ].map((example) => (
              <Grid item xs={12} sm={6} key={example}>
                <Paper
                  elevation={0}
                  onClick={() => setQuery(example)}
                  sx={{
                    bgcolor: 'primary.50',
                    p: 1,
                    borderRadius: 1,
                    cursor: 'pointer',
                    borderLeft: '2px solid',
                    borderLeftColor: 'primary.main',
                    height: '100%',
                    '&:hover': {
                      bgcolor: 'primary.100',
                      transform: 'translateX(2px)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <Typography variant="caption">"{example}"</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Paper>
  );
};

const CategoryBrowser = ({ onCategorySelect, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);

  if (isLoading) {
    return (
      <Paper variant='outlined' sx={{ p: 2, border: 'none' }}>
        <Skeleton variant="text" width="60%" height={32} />
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant="rectangular" height={50} sx={{ mt: 1, borderRadius: 1 }} />
        ))}
      </Paper>
    );
  }

  return (
    <aside aria-label="Browse business categories">
      <Paper variant='outlined' sx={{ p: 2, border: 'none' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: isExpanded ? 2 : 0
        }}>
          <Typography 
            variant="h6" 
            component="h2"
          >
            Browse by Category
          </Typography>
          {isMobile && (
            <IconButton 
              onClick={() => setIsExpanded(!isExpanded)}
              size="small"
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
        
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {CATEGORIES.map((category) => {
              const Icon = category.icon;
              return (
                <Paper
                  key={category.name}
                  elevation={0}
                  onClick={() => onCategorySelect(category.name)}
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Browse ${category.name} businesses`}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Icon sx={{ 
                        mr: 2, 
                        color: category.color,
                        fontSize: 24 
                      }} />
                      <Typography>{category.name}</Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Collapse>
      </Paper>
    </aside>
  );
};

const QuickLinks = ({ onQuickLinkSelect, isLoading }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(!isMobile);

  useEffect(() => {
    setIsExpanded(!isMobile);
  }, [isMobile]);

  if (isLoading) {
    return (
      <Paper variant='outlined' sx={{ p: 2, border: 'none' }}>
        <Skeleton variant="text" width="60%" height={32} />
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mt: 1, borderRadius: 1 }} />
        ))}
      </Paper>
    );
  }

  return (
    <aside aria-label="Quick discovery links">
      <Paper variant='outlined' sx={{ p: 2, border: 'none' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: isExpanded ? 2 : 0
        }}>
          <Typography 
            variant="h6" 
            component="h2"
          >
            Quick Links
          </Typography>
          {isMobile && (
            <IconButton 
              onClick={() => setIsExpanded(!isExpanded)}
              size="small"
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
        
        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {QUICK_LINKS.map((link) => {
              const Icon = link.icon;
              return (
                <Paper
                  key={link.title}
                  elevation={0}
                  onClick={() => onQuickLinkSelect(link.tag)}
                  sx={{
                    p: 2,
                    bgcolor: 'grey.50',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'grey.100',
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Discover ${link.title.toLowerCase()}`}
                >
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                    <Icon sx={{ 
                      color: link.color,
                      fontSize: 24 
                    }} />
                    <Box>
                      <Typography variant="subtitle2">{link.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {link.description}
                      </Typography>
                    </Box>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        </Collapse>
      </Paper>
    </aside>
  );
};

const FindBusinessPage = () => {
  const navigate = useNavigate();

  const token = useSelector((state) => state.auth.token);  
  const [isLoading, setIsLoading] = useState(true);

  const { data: trendingData, isLoading: isTrendingLoading } = useGetUserTrendingQuery();
  console.log('trendingData', trendingData);

  const args = {   
    authType: 'none',
    token: token
  };

  const { data: topBusinessesData, isLoading: isTopBusinessesLoading } = useGetTopBusinessesQuery(args);
  console.log('topBusinessesData', topBusinessesData);

  const generateWebsiteSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": "https://www.revfer.com",
      "name": "Revfer Business Directory",
      "description": "AI-powered local business discovery platform with authentic community reviews",
      "url": "https://www.revfer.com",
      "potentialAction": [
        {
          "@type": "SearchAction",
          "target": {
            "@type": "EntryPoint",
            "urlTemplate": "https://www.revfer.com/searchresults?query={search_term_string}&location={location}"
          },
          "query-input": "required name=search_term_string"
        }
      ],
      "publisher": {
        "@type": "Organization",
        "name": "Revfer Inc."
      }
    };
  };

  const generateBusinessDirectorySchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "Business Categories",
      "description": "Browse local businesses by category",
      "numberOfItems": CATEGORIES.length,
      "itemListElement": CATEGORIES.map((category, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": category.name,
        "description": `Find local ${category.name.toLowerCase()} businesses`,
        "url": `https://www.revfer.com/category/${category.name.toLowerCase()}`
      }))
    };
  };

  const generateFAQSchema = () => {
    return {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "How do I find trusted local businesses?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Use our AI-powered search to find businesses by category or describe what you're looking for in natural language. Browse by popular categories like restaurants, dentists, lawyers, and hotels, or discover highly-rated businesses through community recommendations."
          }
        },
        {
          "@type": "Question",
          "name": "What makes Revfer different from other business directories?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Revfer uses AI technology to provide smarter business recommendations based on authentic community reviews and trust networks. Our natural language search lets you describe exactly what you're looking for, and our smart ratings consider reviewer credibility and connections."
          }
        },
        {
          "@type": "Question",
          "name": "Can I search for businesses using natural language?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes! Our AI-powered natural language search lets you describe what you're looking for in your own words, like 'cozy coffee shop with strong wifi' or 'family-friendly dentist open on weekends'. This helps you find businesses that match your specific needs."
          }
        },
        {
          "@type": "Question",
          "name": "What types of businesses can I find on Revfer?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "You can find all types of local businesses including restaurants, coffee shops, dentists, doctors, lawyers, insurance agents, hotels, and many more. Browse by category or search for specific services in your area."
          }
        },
        {
          "@type": "Question",
          "name": "How do I search for businesses in my area?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Enter your city and state in the location field, then search by business type or describe what you're looking for. You can use traditional keyword search or our AI-powered natural language search for more specific requests."
          }
        }
      ]
    };
  };

  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.innerHTML = styles;
    document.head.appendChild(styleElement);
    
    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    setIsLoading(isTrendingLoading || isTopBusinessesLoading);
  }, [isTrendingLoading, isTopBusinessesLoading]);

  const handleSearch = async ({ query, location, isNaturalLanguageSearch }) => {
    console.log('Searching:', { query, location, isNaturalLanguageSearch });
    const userFlag = false;
    
    navigate('/searchresults', { 
      state: { 
        query, 
        location, 
        userFlag,
        isNaturalLanguageSearch
      } 
    });
  };

  const handleCategorySelect = (query) => {
    console.log('Category selected:', query);
    const userFlag = true;
    navigate('/searchresults', { state: { query, userFlag } });
  };

  const handleQuickLinkSelect = (query) => {
    console.log('Quick link selected:', query);
    const userFlag = true;
    navigate('/searchresults', { state: { query, userFlag } });
  };

  const websiteSchema = generateWebsiteSchema();
  const directorySchema = generateBusinessDirectorySchema();
  const faqSchema = generateFAQSchema();
  
  const pageTitle = "Find Local Businesses - AI-Powered Business Directory | Revfer";
  const pageDescription = "Discover trusted local businesses with AI-powered search. Find restaurants, dentists, lawyers, hotels and more through authentic community reviews and smart recommendations. Search by category or describe what you need in natural language.";
  const pageKeywords = [
    "local business directory",
    "find businesses near me", 
    "business finder",
    "local business search",
    "AI business search",
    "natural language business search",
    "restaurants near me",
    "local dentists",
    "lawyers near me",
    "local doctors",
    "insurance agents",
    "hotels near me",
    "business reviews",
    "trusted businesses",
    "community recommendations",
    "smart business search",
    "authentic reviews",
    "local business discovery"
  ].join(", ");

  return (
    <>
      <EnhancedSEO 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        type="website"
        image="/og-image.jpg"
        author="Revfer Discovery Team"
        dateModified={new Date().toISOString()}
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Find Businesses', url: '/find' }
        ]}
      />

      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(directorySchema)}
        </script>
        
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>

        <meta name="page-type" content="BusinessDirectory" />
        <meta name="search-functionality" content="AI-powered, Natural Language, Category Browse" />
        <meta name="business-categories" content="Restaurants, Dentists, Lawyers, Doctors, Insurance, Hotels" />
        <meta name="search-features" content="Natural Language Search, AI Recommendations, Community Reviews" />
        
        <meta name="geo.region" content="US" />
        <meta name="geo.placename" content="United States" />
        <meta name="local-business-directory" content="AI-powered local business discovery" />
        
        <meta name="ai-features" content="Natural language search, smart recommendations, community trust signals" />
        <meta name="search-types" content="keyword search, natural language search, category browse" />
        <meta name="directory-scope" content="Local businesses, professional services, restaurants, healthcare" />
      </Helmet>

      <main itemScope itemType="https://schema.org/WebSite">
        <Container maxWidth="xl" sx={{ py: 3 }}>
          <Typography 
            component="h1" 
            sx={{ 
              position: 'absolute', 
              left: '-10000px', 
              top: 'auto', 
              width: '1px', 
              height: '1px', 
              overflow: 'hidden' 
            }}
          >
            Find Local Businesses - AI-Powered Business Directory
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <CategoryBrowser
                onCategorySelect={handleCategorySelect}
                isLoading={isLoading}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <SearchBar
                onSearch={handleSearch}
                isLoading={isLoading}
              />
              <TrendingAndTrustSignals 
                trendingBusinesses={trendingData}
                topBusinesses={topBusinessesData}
                isLoading={isTrendingLoading || isTopBusinessesLoading} 
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <QuickLinks
                onQuickLinkSelect={handleQuickLinkSelect}
                isLoading={isLoading}
              />
            </Grid>
          </Grid>
        </Container>
      </main>
    </>
  );
};

export default FindBusinessPage;